import Horn, { HornDevTool } from '@mtfe/horn-sdk'
import { getUuid } from '@RootCommon/utils'
import { defaultConfig } from './defaultConfig'
import { loganLog } from '@RootCommon/utils/logan'

const HORN_KEY = 'sailor_b_pc_config'

interface InitOptions {
  isDev?: boolean // 是否使用测试环境，默认 false
  disableRegister?: boolean // 是否禁用通过 Horn.register 获取配置功能，默认 false，不禁用
  ignoreStorageError?: boolean // 是否忽略存储错误，默认 false
  sankuaiOnly?: boolean // 是否是仅内网可以访问的配置，默认为 false
  host?: string // 请求的 host 地址，需是 horn 后端配置的可用地址
  enableDevtool?: boolean // 是否启用开发工具（仅开发环境使用）
  token?: string // perf 平台的 token 【可选】
}

export class HornConfig {
  private static instance: HornConfig | null = null
  private isInitialized = false
  private cachedConfig: typeof defaultConfig | null = null

  /**
   * 获取 HornConfig 单例实例
   */
  public static getInstance (): HornConfig {
    if (!HornConfig.instance) {
      HornConfig.instance = new HornConfig()
    }
    return HornConfig.instance
  }

  /**
   * 初始化 Horn SDK
   * @param options 初始化配置选项
   */
  public init (options: InitOptions = {}) {
    if (this.isInitialized) {
      console.warn('Horn SDK has already been initialized')
      return
    }

    const { isDev = false, disableRegister = false, ignoreStorageError = false, sankuaiOnly = false, host, enableDevtool = false, token } = options

    const initParams = {
      id: getUuid(), // 如果需要使用 Horn 灰度功能，则此字段必填，建议传入 UUID 或相同性质的用户唯一识别符
      ...(token && { token }),
    }

    const config = {
      ...(isDev !== undefined && { isDev }),
      disableRegister, // 是否禁用通过 Horn.register 获取配置功能
      ignoreStorageError,
      sankuaiOnly, // 是否是仅内网可以访问的配置
      ...(host && { host }),
      /**
       * 版本要求 >= 0.2.4
       * 传入horn 开发者模块实例，传入之后会劫持 horn 实例的请求，可以设置独立开发测试环境，设置后会请求独立测试环境的配置内容
       * ！！！！！线上环境请勿使用此配置项！！！！！！
       */
      ...(enableDevtool && { devtool: new HornDevTool(Horn) }),
    }

    try {
      Horn.init(initParams, config)
      this.isInitialized = true
      loganLog({ msg: 'Horn SDK 初始化成功' })
    } catch (error) {
      console.error('Failed to initialize Horn SDK:', error)
      loganLog({ msg: 'Horn SDK 初始化失败', error })
      throw error
    }
  }

  // 获取 Horn 配置
  public async getConfig (): Promise<typeof defaultConfig> {
    if (this.cachedConfig) {
      return this.cachedConfig
    }

    if (!this.isInitialized) {
      console.error('Horn SDK has not been initialized.')
      return defaultConfig
    }

    try {
      const config = await Horn.fetch(HORN_KEY)
      if (!config) {
        return defaultConfig
      }
      this.cachedConfig = config
      return config
    } catch (error) {
      console.error('Failed to get horn config', error)
      return defaultConfig
    }
  }

  /**
   * 检查是否已初始化
   */
  public isReady (): boolean {
    return this.isInitialized
  }
}

export function initHorn () {
  const hornConfig = HornConfig.getInstance()
  hornConfig.init({
    isDev: process.env.NODE_ENV === 'development',
    enableDevtool: process.env.NODE_ENV === 'development',
  })
}

export async function getHornConfig () {
  const hornConfig = HornConfig.getInstance()
  if (!hornConfig.isReady()) {
    console.error('Horn SDK not ready')
    return defaultConfig
  }
  const config = await hornConfig.getConfig()
  // todo: 处理 config
  console.log('horn config', config)
  loganLog({ msg: 'getHornConfig 已返回', config })
  return config
}
